/* Atlas V3 Desktop — My Stocks: the personal-money cluster, mirroring the
   mobile WATCHLIST / MY AGENT / ACCOUNTS split (mobile uses routed screens +
   DeskSwitcher; desktop nav is local-state so the cluster is three tabs here).
   Watchlist: the union of the user's lists + Atlas metadata + live prices.
   My Agent: the embedded Agent Desk (agent-synced brokerage mirror).
   Accounts: the manually-fed portfolio (screenshot import stays mobile). */
import { useEffect, useMemo, useState } from 'react'
import { useUser, useAuth } from '@clerk/clerk-react'
import { StockLogo } from '@/v2/components/StockLogo'
import { Spark, intent, useTicker } from '@/v2/v3/lib'
import { DotGlobe } from '@/v2/v3/DotGlobe'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { useWatchlistMeta } from '@/v2/hooks/useWatchlistMeta'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { useTickerBars } from '@/v2/hooks/useChartBars'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useAdrUniverse } from '@/v2/hooks/useAdrUniverse'
import { useLeveragedEtfs } from '@/v2/hooks/useLeveragedEtfs'
import { Crossfade, SkelRowD, SkelText, enterStyle } from '../qol'
import { AgentDesk } from './AgentDesk'

function pct(v) { return v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` }
const VERDICT = { BULLISH: '--studio-success', BEARISH: '--studio-danger', NEUTRAL: '--studio-text-subtle' }

export function MyStocks() {
  const { watchlists } = useWatchlists()
  const wlCount = useMemo(() => new Set((watchlists || []).flatMap((w) => w.tickers || [])).size, [watchlists])
  const [tab, setTab] = useState('watchlist')
  return (
    <>
      <div className="v3web-phead">
        <div className="k">Your Positions</div>
        <div className="h" style={{ fontStyle: 'italic' }}>My Stocks</div>
      </div>
      <div style={{ display: 'flex', gap: 18, marginBottom: 18 }}>
        {[['watchlist', 'WATCHLIST', wlCount], ['agent', 'MY AGENT', null], ['accounts', 'ACCOUNTS', null]].map(([k, l, n]) => (
          <button key={k} onClick={() => setTab(k)} style={{ all: 'unset', cursor: 'pointer', fontFamily: 'var(--studio-font-mono)', fontSize: 11, letterSpacing: '0.1em', paddingBottom: 8, borderBottom: tab === k ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent', color: tab === k ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-muted))' }}>{l}{n != null ? <span style={{ color: 'hsl(var(--studio-text-subtle))' }}> {n}</span> : ''}</button>
        ))}
      </div>
      {tab === 'watchlist' ? <WatchlistPanel watchlists={watchlists} /> : tab === 'agent' ? <AgentDesk embedded /> : <HoldingsPanel />}
    </>
  )
}

function WatchlistPanel({ watchlists }) {
  const openTicker = useTicker()
  const { removeTicker, loading: wlLoading } = useWatchlists()
  const metaFor = useWatchlistMeta() // returns the metaFor(ticker) fn directly
  // metaFor joins against the ACTIVE universe only — anything a user can star
  // from the other desks comes back empty from it. Resolve names/known-ness
  // against EVERY Atlas source so all watchlisted tickers render as real rows:
  // main+hunting stocks (GEM chip), the ADR catalog (GLOBAL chip), and the 2×
  // leveraged ETFs (2× chip). Only tickers in NO table read as uncovered.
  const { stocks: allStocks, loading: uniLoading } = useAtlasStocks({ includeInactive: true })
  const { us: adrUs, home: adrHome } = useAdrUniverse()
  const { instruments: levInstruments } = useLeveragedEtfs()
  const sourceByTicker = useMemo(() => {
    const m = {}
    for (const s of allStocks) m[s.ticker] = { name: s.name || s.company, sector: s.sector, chip: s.tier && s.tier !== 'main' ? 'GEM' : null }
    for (const s of [...adrUs, ...adrHome]) if (!m[s.ticker]) m[s.ticker] = { name: s.company, sector: s.sector, chip: 'GLOBAL' }
    for (const inst of levInstruments) {
      for (const e of inst.etfs || []) {
        if (e.ticker && !m[e.ticker]) m[e.ticker] = { name: `${e.issuer || ''} ${e.leverage || 2}× Long ${inst.underlying}`.trim(), sector: inst.sector, chip: '2×' }
      }
    }
    return m
  }, [allStocks, adrUs, adrHome, levInstruments])
  const tickers = useMemo(() => [...new Set((watchlists || []).flatMap((w) => w.tickers || []))], [watchlists])

  // Desktop remove affordance (mobile uses swipe): drop the ticker from every
  // list that contains it — the panel shows the union.
  const removeEverywhere = (t) => {
    for (const w of watchlists || []) {
      if ((w.tickers || []).includes(t)) removeTicker(w.id, t)
    }
  }
  const { prices } = useLivePrices(tickers)
  // 1D to match the 1D % (c, below) this row displays — was hardcoded '5D',
  // which never matched the shown %.
  const { barsByTicker } = useTickerBars(tickers, '1D')

  const rows = useMemo(() => tickers.map((t) => {
    const m = metaFor(t) || {}
    const src = sourceByTicker[t]
    const c = prices[t]?.change1d ?? m.change1d
    const name = (m.name && m.name !== t) ? m.name : (src?.name || null)
    return {
      t, name, sector: m.sector || src?.sector || null, verdict: m.verdict,
      chip: src?.chip || null, known: !!(name || src),
      price: prices[t]?.price ?? m.price, c, bars: barsByTicker[t],
    }
  }).sort((a, b) => Math.abs(b.c ?? 0) - Math.abs(a.c ?? 0)), [tickers, prices, barsByTicker, metaFor, sourceByTicker])

  // Hold the skeleton while the coverage sources are unresolved too — without
  // this every row first paints dimmed "Not in Atlas coverage", then flips to a
  // real row when the universe lands (known-flag flash).
  if ((tickers.length === 0 && wlLoading) || (tickers.length > 0 && uniLoading && allStocks.length === 0)) {
    return <Crossfade loading skeleton={<div>{[0, 1, 2, 3, 4].map((i) => <SkelRowD key={i} i={i} />)}</div>} />
  }
  if (tickers.length === 0) {
    return (
      <div className="v3web-empty">
        <DotGlobe size={170} style={{ margin: '0 auto 20px' }} />
        <div className="pe-k">Your watchlist is empty</div>
        <p>Open any ticker and tap the star to start tracking it.</p>
      </div>
    )
  }
  return (
    <div>
      {rows.map((r, ri) => {
        const up = (r.c ?? 0) >= 0
        // Only a ticker in NO Atlas table (genuinely delisted / typo) renders
        // dimmed + non-clickable. Hunting-tier names are real — full row.
        const known = r.known
        return (
          <div key={r.t} className="v3web-row" onClick={known ? () => openTicker(r.t) : undefined}
            style={known ? enterStyle(ri) : { cursor: 'default', opacity: 0.55 }}>
            <StockLogo ticker={r.t} size={26} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="nm">{r.t}{r.name && <span style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 12 }}> {r.name}</span>}</div>
              <div className="sub">{known
                ? <>{r.sector}{r.chip ? <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.35)', borderRadius: 3, padding: '1px 5px', marginLeft: r.sector ? 6 : 0 }}>{r.chip}</span> : ''}{r.verdict ? <span style={{ color: `hsl(var(${VERDICT[r.verdict] || '--studio-text-subtle'}))`, marginLeft: 6 }}>{r.verdict}</span> : ''}</>
                : 'Not in Atlas coverage'}</div>
            </div>
            {known && <Spark data={r.bars} up={up} width={60} height={24} />}
            {known && r.price != null && <div className="v3web-num" style={{ fontSize: 12, color: 'hsl(var(--studio-text-muted))', width: 76, textAlign: 'right' }}>${Number(r.price).toFixed(2)}</div>}
            {known && <div className="v3web-num" style={{ fontSize: 13, color: `hsl(var(${intent(up)}))`, width: 76, textAlign: 'right' }}>{pct(r.c)}</div>}
            <button
              onClick={(e) => { e.stopPropagation(); removeEverywhere(r.t) }}
              aria-label={`Remove ${r.t}`} title="Remove from watchlist"
              style={{ all: 'unset', cursor: 'pointer', padding: '2px 4px', color: 'hsl(var(--studio-text-subtle))', fontSize: 13, lineHeight: 1 }}
            >✕</button>
          </div>
        )
      })}
    </div>
  )
}

/* Holdings — full consolidation, mirroring mobile: /api/portfolio uploads with
   SHARES, consolidated by ticker → total market value, day P/L $+%, allocation
   strip, concentration line, and per-position rows (shares · value · move).
   Screenshot import stays a mobile flow. */
function HoldingsPanel() {
  const openTicker = useTicker()
  const { user, isLoaded } = useUser()
  const { getToken } = useAuth()
  const userId = user?.id || null
  const [uploads, setUploads] = useState(null) // null = loading

  useEffect(() => {
    if (!isLoaded) return
    if (!userId) { setUploads([]); return }
    let cancelled = false
    ;(async () => {
      try {
        const token = await getToken()
        if (cancelled) return
        const r = await fetch('/api/portfolio?action=read', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
        const j = await r.json().catch(() => ({}))
        if (!cancelled) setUploads(r.ok && Array.isArray(j.uploads) ? j.uploads : [])
      } catch { if (!cancelled) setUploads([]) }
    })()
    return () => { cancelled = true }
  }, [userId, isLoaded, getToken])

  const sharesByTicker = useMemo(() => {
    const shares = {}
    for (const u of (uploads || [])) {
      for (const h of (u.holdings || [])) {
        shares[h.ticker] = (shares[h.ticker] || 0) + (Number(h.shares) || 0)
      }
    }
    return shares
  }, [uploads])

  const { stocks } = useAtlasStocks({ includeInactive: true })
  const nameFor = useMemo(() => Object.fromEntries(stocks.map((s) => [s.ticker, s.name || s.company])), [stocks])
  const tickers = useMemo(() => Object.keys(sharesByTicker), [sharesByTicker])
  const { prices, loading: pricesLoading } = useLivePrices(tickers)
  // 1D to match the 1D % (q.change1d, below) this row displays — was
  // hardcoded '5D', which never matched the shown %.
  const { barsByTicker } = useTickerBars(tickers, '1D')

  const rows = useMemo(() => {
    const rs = []
    for (const [t, shares] of Object.entries(sharesByTicker)) {
      const q = prices[t]
      if (!q?.price) continue
      const val = q.price * shares
      // change1d can be legitimately null from /api/prices — keep it null,
      // never fabricate 0 (seam ≠ fallback).
      rs.push({ t, name: nameFor[t] || t, shares, price: q.price, c: q.change1d ?? null, val, bars: barsByTicker[t] ?? null })
    }
    return rs.sort((a, b) => b.val - a.val)
  }, [sharesByTicker, prices, nameFor, barsByTicker])

  const total = rows.reduce((a, r) => a + r.val, 0)
  // Day P/L over positions that actually have a change% — null when none do
  const pricedRows = rows.filter((r) => r.c != null)
  const pricedVal = pricedRows.reduce((a, r) => a + r.val, 0)
  const dayPL = pricedRows.length ? pricedRows.reduce((a, r) => a + r.val * (r.c / 100), 0) : null
  const dayPct = dayPL != null && pricedVal ? (dayPL / pricedVal) * 100 : null
  const dayUp = (dayPL ?? 0) >= 0

  const loading = uploads === null || (tickers.length > 0 && pricesLoading && rows.length === 0)

  if (!isLoaded || loading) {
    return (
      <Crossfade loading skeleton={
        <div>
          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 9 }}>
            <SkelText w={92} h={9} />
            <SkelText w={190} h={30} />
            <SkelText w={140} h={11} />
          </div>
          {[0, 1, 2, 3].map((i) => <SkelRowD key={i} i={i} />)}
        </div>
      } />
    )
  }
  if (!userId) return <div className="v3web-empty"><div className="pe-k">Sign in to see your holdings</div></div>
  if (rows.length === 0) return <div className="v3web-empty"><div className="pe-k">No positions yet</div><p>Import a brokerage screenshot from the mobile app — Atlas reads it once and discards the image.</p></div>

  const Mono = { fontFamily: 'var(--studio-font-mono)' }
  return (
    <div>
      {/* book header: positions · total value · day P/L · allocation · concentration */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))', textTransform: 'uppercase' }}>{rows.length} positions</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginTop: 5 }}>
          <span style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 36, lineHeight: 1, color: 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums' }}>
            ${total.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </span>
          {dayPL != null && (
            <span style={{ ...Mono, fontSize: 14, color: `hsl(var(${intent(dayUp)}))`, fontVariantNumeric: 'tabular-nums' }}>
              {dayUp ? '+' : '−'}${Math.abs(dayPL).toLocaleString('en-US', { maximumFractionDigits: 0 })} · {dayUp ? '+' : ''}{dayPct.toFixed(1)}%
            </span>
          )}
        </div>
        <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginTop: 5 }}>TODAY · MARKET VALUE</div>
      </div>

      {rows.map((r, ri) => {
        const up = (r.c ?? 0) >= 0
        return (
          <div key={r.t} className="v3web-row" onClick={() => openTicker(r.t)} style={enterStyle(ri)}>
            <StockLogo ticker={r.t} size={26} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="nm">{r.t} <span style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 12 }}>{r.name}</span></div>
              <div className="sub">{r.shares.toLocaleString('en-US', { maximumFractionDigits: 2 })} sh · ${r.price.toFixed(2)}</div>
            </div>
            <Spark data={r.bars} up={up} width={56} height={22} />
            <div className="v3web-num" style={{ fontSize: 12.5, color: 'hsl(var(--studio-text))', width: 96, textAlign: 'right' }}>${r.val.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            <div className="v3web-num" style={{ fontSize: 13, color: r.c != null ? `hsl(var(${intent(up)}))` : 'hsl(var(--studio-text-subtle))', width: 76, textAlign: 'right' }}>{pct(r.c)}</div>
          </div>
        )
      })}
    </div>
  )
}
